import React, { useState, useContext, useCallback } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { MLAlertGroupComponent} from 'ml-components-common-react';
import { MLTagManagerService } from 'ml-portals-services-react';
import { ClaimService } from 'gw-capability-claim';
import appConfig from 'app-config';

import metadata from './MLClaimDetailsUploadDocumentPopup.metadata.json5';
import styles from './MLClaimDetailsUploadDocumentPopup.module.scss';
import messages from './MLClaimDetailsUploadDocumentPopup.messages'

const MLClaimDetailsUploadDocumentPopup = (props) => {
    const {
        claim,
        onCancel,
        onSave,
    } = props;

    const { authHeader } = useAuthentication();
    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);

    const { onValidate, isComponentValid } = useValidation('MLClaimDetailsUploadDocumentPopup');
    const { unacceptableFileExt, maxFileUploadSize } = appConfig
    const [uploadingDoc, updateUploadingDoc] = useState(
        viewModelService.create(
            {
                'DocUID': '001',
                'DocumentType': 'fnol',
                'documentType_ML': 'clm_customer_uploads',
                'SecurityType': 'unrestricted',
                'Status': 'approved',
                'Author': 'Policy Holder',
                'claimNumber': claim.claimNumber
            },
            'cc',
            'edge.capabilities.claim.document.dto.ClaimDocumentUploadDTO'
        )
    );
    const [fileData, updateFileData] = useState({});
    const [showErrors, setShowErrors] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isFailUploadFile, setIsFailUploadFile] = useState(false);

    const handleSave = async () => { 
        if (!isComponentValid) {
            setShowErrors(true);
            return;        
        }

        const documentUploadTokenId = await ClaimService.claimsDocUploadToken(undefined, authHeader);

        const fileName = fileData.name;
        const tmpArray = fileName.split('.');
        let fileExt = {};
            
        if (tmpArray.length > 0) {
            fileExt = tmpArray[tmpArray.length - 1];
        }
        const acceptableFileExt = fileExt && !unacceptableFileExt.includes(fileExt);
        const fileSizeValid = fileData.size <= maxFileUploadSize;

        if(acceptableFileExt && fileSizeValid){
            const newUploadingDoc = viewModelService.clone(uploadingDoc);
            newUploadingDoc.value.description = fileData.name;
            newUploadingDoc.value.name = fileData.name;
            newUploadingDoc.value.mimeType = fileData.type;
            newUploadingDoc.value.sessionID =  documentUploadTokenId
            
            try{
                const formValues = {
                    type: _.get(uploadingDoc, 'documentSubType_ML.value.name')
                };
                const uploadResponse = await ClaimService.uploadDocument(fileData, newUploadingDoc.value, authHeader)
                MLTagManagerService.sendFormCompleteEvent(`${MLTagManagerService.getFormName()}_Upload Documents`, formValues);
                onSave(uploadResponse);
            } catch (error) {
                console.error(error)
            }
                
        } else {
            setErrorMessage(acceptableFileExt ? messages.invalidFileSize : messages.invalidFileType)
            setIsFailUploadFile(true)
        } 
    }

    const onValueChange = (value, path) => {
        const newVM = viewModelService.clone(uploadingDoc);
        _.set(newVM, path, value);
        updateUploadingDoc(newVM);
    }

    const onUploadDocument = useCallback(
        (file) => {
            file.status = 'selected';
            updateFileData(file)
        },
        []
    );

    const onDocumentChange = () => {
        if (fileData) {
            updateFileData({});
        }
    }

    const handleDrag = (event) => {
        event.preventDefault();
        event.stopPropagation();
    }

    const handleDrop = (event) => {
        event.preventDefault();
        const fileName = event.target.value || event.dataTransfer.files[0].name;
        console.info(`Dragged ${fileName}`);
        const file = event.dataTransfer ? event.dataTransfer.files[0] : event.target.files[0];
        onUploadDocument(file);
    }
    

    const overrideProps = {
        warningInlineNotification: {
            visible: false
        },
        successInlineNotification: {
            visible: false
        },
        errorInlineNotification: {
            message: errorMessage,
            visible: isFailUploadFile
        },
        type: {
            value: uploadingDoc.documentSubType_ML.value
        },
        uploadedFileNameContainer: {
            visible: !_.isUndefined(fileData),
            content: fileData.name
        },
        footerMessageRow: {
            visible: _.get(fileData, 'status') === 'selected'
        },
        footerMessage: {
            content: <>{translator(messages.footerMessage, {
                strong: (chunks) => <strong>{chunks}</strong>,
                filename: _.get(fileData, 'name')
            })} </>
        },
        saveButton: {
            disabled: !_.get(uploadingDoc, 'documentSubType_ML.value') || _.get(fileData, 'status') !== 'selected'
        }
    };

    const resolvers = {
        resolveComponentMap: {
            MLAlertGroupComponent
        },
        resolveCallbackMap: {         
            onClose: onCancel,
            onSave: handleSave,
            onUploadDocument: onUploadDocument,
            onDocumentChange: onDocumentChange,
            handleDrag,
            handleDrop
        },
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            model={uploadingDoc}
            onValueChange={onValueChange}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={styles}
            onValidationChange={onValidate}
            showErrors={showErrors}
        />
    );
}


MLClaimDetailsUploadDocumentPopup.propTypes = {
    policy: PropTypes.shape({}),
    policyNumber: PropTypes.string.isRequired,
}

export default MLClaimDetailsUploadDocumentPopup;